/* eslint-disable react/jsx-max-depth, react/jsx-no-bind */
import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MaterialIcon } from '@dsny/dsny-component-library'
import dropdownStyles from 'components/Dropdowns/Dropdowns.module.scss'
import { QuickViewGhostButton } from './QuickViewModal.styles'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'

const menuContentClassName = [
  dropdownStyles.DropdownMenuContent,
  dropdownStyles.ReleaseInfoActionsDropdown
].join(' ')

const menuItemClassName = [
  dropdownStyles.DropdownMenuItem,
  dropdownStyles.ReleaseInfoActionsItem
].join(' ')

export default function QuickViewReleaseMenu({
  playlists,
  canShareRelease,
  disabled,
  isFlagged,
  isArchived,
  isPlayDisabled,
  isPlayNextDisabled,
  isPlayLaterDisabled,
  onPlay,
  onPlayNext,
  onPlayLater,
  onAddToPlaylist,
  onCreatePlaylist,
  onShare,
  onContact,
  onFlag,
  onArchive
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <QuickViewGhostButton
          aria-label={t('general.more-actions')}
          disabled={disabled}
          type="button"
        >
          <MaterialIcon name="MoreVertical" color={theme.colors.neutralW90} />
        </QuickViewGhostButton>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          avoidCollisions
          sideOffset={8}
          style={{
            background: theme.colors.neutralW20,
            color: theme.colors.neutralW100
          }}
          className={menuContentClassName}
        >
          <DropdownMenu.Item
            className={menuItemClassName}
            disabled={isPlayDisabled}
            onClick={onPlay}
          >
            {t('general.play')}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={menuItemClassName}
            disabled={isPlayNextDisabled}
            onClick={onPlayNext}
          >
            {t('general.play-next')}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={menuItemClassName}
            disabled={isPlayLaterDisabled}
            onClick={onPlayLater}
          >
            {t('general.play-later')}
          </DropdownMenu.Item>

          {!(archiveToFilterEnabled && isArchived) && (
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger className={menuItemClassName}>
                {t('general.addto')}
                <div className={dropdownStyles.RightSlot}>
                  <MaterialIcon
                    name="KeyboardArrowRight"
                    color={theme.colors.neutralW100}
                  />
                </div>
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent
                  avoidCollisions
                  sideOffset={2}
                  alignOffset={-5}
                  style={{
                    background: theme.colors.neutralW20,
                    color: theme.colors.neutralW100
                  }}
                  className={menuContentClassName}
                >
                  {playlists?.map((playlist) => (
                    <DropdownMenu.Item
                      key={playlist && playlist.playlistid}
                      className={menuItemClassName}
                      onClick={() => onAddToPlaylist(playlist)}
                    >
                      {playlist && playlist.name}
                    </DropdownMenu.Item>
                  ))}
                  <DropdownMenu.Item
                    className={menuItemClassName}
                    onClick={onCreatePlaylist}
                  >
                    New playlist
                  </DropdownMenu.Item>
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          )}

          {canShareRelease && onShare && (
            <DropdownMenu.Item className={menuItemClassName} onClick={onShare}>
              {t('general.share')}
            </DropdownMenu.Item>
          )}

          {onContact && (
            <DropdownMenu.Item
              className={menuItemClassName}
              onClick={onContact}
            >
              Contact
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Item className={menuItemClassName} onClick={onFlag}>
            {isFlagged ? t('general.unflag') : t('general.flag')}
          </DropdownMenu.Item>
          <DropdownMenu.Item className={menuItemClassName} onClick={onArchive}>
            {isArchived ? t('general.restore') : t('general.archive')}
          </DropdownMenu.Item>

          <DropdownMenu.Arrow
            className={dropdownStyles.DropdownMenuArrow}
            style={{ fill: theme.colors.neutralW20 }}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

QuickViewReleaseMenu.propTypes = {
  playlists: PropTypes.arrayOf(PropTypes.object),
  canShareRelease: PropTypes.bool,
  disabled: PropTypes.bool,
  isFlagged: PropTypes.bool,
  isArchived: PropTypes.bool,
  isPlayDisabled: PropTypes.bool,
  isPlayNextDisabled: PropTypes.bool,
  isPlayLaterDisabled: PropTypes.bool,
  onPlay: PropTypes.func,
  onPlayNext: PropTypes.func,
  onPlayLater: PropTypes.func,
  onAddToPlaylist: PropTypes.func,
  onCreatePlaylist: PropTypes.func,
  onShare: PropTypes.func,
  onContact: PropTypes.func,
  onFlag: PropTypes.func,
  onArchive: PropTypes.func
}
